// Renderer Alun-alun — kamera isometrik 2:1 yang bisa DIPUTAR (yaw) di atas bidang tanah datar.
// Koordinat dunia tetap 2D (sama dengan server: jalur, bangku, rintangan). Tanah & tapak digambar lewat transform
// bidang (rotasi + pipih), objek 3D (balok / silinder / kerucut) diproyeksikan per sudut kamera, karakter, pohon &
// lampu sebagai sprite tegak (billboard); nama / gelembung obrolan / emote digambar di ruang layar agar tetap terbaca.
import { drawAvatar, drawEmoteGlyph, shade } from "./avatarDraw";
import { posisiPemain } from "./alunSim";

const FONT = "'Plus Jakarta Sans', system-ui, sans-serif";
export const EMOTE_MS = 2600, BUBBLE_MS = 6500;
const SQ = 0.5;                       // pipih sumbu kedalaman (2:1 seperti peta kota)
const ROT_AWAL = Math.PI / 4;         // 45° → plaza tampil sebagai belah ketupat
const TAU = Math.PI * 2;
const hash01 = s => { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return ((h >>> 0) % 10000) / 10000; };
const WARNA_LAPAK_DAGANG = ["#f4f1e6", "#d9a21b", "#dc2626", "#2e9e5b"];

export function createAlunRenderer(canvas) {
  const ctx = canvas.getContext("2d");
  const cam = { x: 480, y: 560, zoom: 1.25, rot: ROT_AWAL, rotTarget: ROT_AWAL };
  let W = 1, H = 1, dpr = 1, follow = true, layout = null, rumput = [];
  let c = Math.cos(cam.rot), s = Math.sin(cam.rot);   // cos/sin sudut kamera (diperbarui tiap frame)
  const fx = [];

  function resize() {
    const r = canvas.getBoundingClientRect();
    dpr = Math.min(2, window.devicePixelRatio || 1); W = Math.max(1, r.width); H = Math.max(1, r.height);
    canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
  }
  const ro = new ResizeObserver(resize); ro.observe(canvas); resize();

  // ---------- Proyeksi ----------
  // dunia (wx, wy, z) → layar. z = ketinggian (px pada zoom 1, lurus ke atas layar)
  const proj = (wx, wy, z = 0) => { const dx = wx - cam.x, dy = wy - cam.y; return [(c * dx - s * dy) * cam.zoom + W / 2, (s * dx + c * dy) * SQ * cam.zoom + H / 2 - z * cam.zoom]; };
  const depth = (wx, wy) => s * (wx - cam.x) + c * (wy - cam.y);          // urutan painter's: makin besar makin depan
  const unproj = (sx, sy) => { const rx = (sx - W / 2) / cam.zoom, ry = (sy - H / 2) / (SQ * cam.zoom); return [cam.x + c * rx + s * ry, cam.y - s * rx + c * ry]; };
  const tanah = () => { ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.translate(W / 2, H / 2); ctx.scale(cam.zoom, cam.zoom * SQ); ctx.rotate(cam.rot); ctx.translate(-cam.x, -cam.y); };
  const layar = () => ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  function clampCam() {
    if (!layout) return;
    cam.x = Math.min(layout.w - 60, Math.max(60, cam.x));
    cam.y = Math.min(layout.h - 60, Math.max(60, cam.y));
  }

  // ---------- Primitif 3D (ruang layar, berjangkar dunia) ----------
  const poly = pts => { ctx.beginPath(); pts.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1]))); ctx.closePath(); ctx.fill(); };
  const elips = (x, y, rx, ry) => { ctx.beginPath(); ctx.ellipse(x, y, rx, Math.max(0.1, ry), 0, 0, TAU); ctx.fill(); };
  const NORMAL = [[0, -1], [1, 0], [0, 1], [-1, 0]];
  // Balok sejajar sumbu dunia: tapak w×d berpusat (cx,cy), alas di z0, tinggi h. Sisi kiri-layar terang, kanan gelap, atap paling terang.
  function balok(cx, cy, w, d, z0, h, col, top) {
    const K = [[cx - w / 2, cy - d / 2], [cx + w / 2, cy - d / 2], [cx + w / 2, cy + d / 2], [cx - w / 2, cy + d / 2]];
    const B = K.map(([x, y]) => proj(x, y, z0)), T = K.map(([x, y]) => proj(x, y, z0 + h)), vis = [];
    for (let i = 0; i < 4; i++) {
      const [nx, ny] = NORMAL[i]; vis[i] = s * nx + c * ny > 0.001;
      if (!vis[i]) continue;
      ctx.fillStyle = shade(col, 0.84 - 0.1 * (c * nx - s * ny));
      const j = (i + 1) % 4; poly([B[i], B[j], T[j], T[i]]);
    }
    ctx.fillStyle = top || shade(col, 1.0); poly(T);
    return { B, T, vis };
  }
  function silinder(cx, cy, r, z0, h, col, top) {
    const [bx, by] = proj(cx, cy, z0), [tx, ty] = proj(cx, cy, z0 + h), rx = r * cam.zoom, ry = rx * SQ;
    const g = ctx.createLinearGradient(bx - rx, 0, bx + rx, 0);
    g.addColorStop(0, shade(col, 0.95)); g.addColorStop(0.5, shade(col, 0.82)); g.addColorStop(1, shade(col, 0.64));
    ctx.fillStyle = g; ctx.beginPath(); ctx.ellipse(bx, by, rx, ry, 0, 0, Math.PI); ctx.lineTo(tx - rx, ty); ctx.ellipse(tx, ty, rx, ry, 0, Math.PI, 0, true); ctx.closePath(); ctx.fill();
    ctx.fillStyle = top || col; elips(tx, ty, rx, ry);
    return [tx, ty];
  }
  function kerucut(cx, cy, r, z0, h, col) {
    const [bx, by] = proj(cx, cy, z0), [ax, ay] = proj(cx, cy, z0 + h), rx = r * cam.zoom, ry = rx * SQ;
    ctx.fillStyle = shade(col, 0.96); ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(bx - rx, by); ctx.ellipse(bx, by, rx, ry, 0, Math.PI, Math.PI / 2, true); ctx.closePath(); ctx.fill();
    ctx.fillStyle = shade(col, 0.72); ctx.beginPath(); ctx.moveTo(ax, ay); ctx.ellipse(bx, by, rx, ry, 0, Math.PI / 2, 0, true); ctx.closePath(); ctx.fill();
  }
  // Teks rata di bidang sisi balok (p0→p1 tepi bawah sisi di layar, hPx tinggi sisi), tetap tegak & terbaca kiri→kanan
  function teksSisi(p0, p1, hPx, text, col, size) {
    if (p0[0] > p1[0]) [p0, p1] = [p1, p0];
    const ex = p1[0] - p0[0], ey = p1[1] - p0[1], L = Math.hypot(ex, ey); if (L < 10) return;
    ctx.save(); ctx.transform(ex / L, ey / L, 0, 1, (p0[0] + p1[0]) / 2, (p0[1] + p1[1]) / 2 - hPx / 2);
    ctx.fillStyle = col; ctx.font = `800 ${size}px ${FONT}`; ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText(text, 0, 0); ctx.restore();
  }
  // Sprite tegak: gambar fn(ctx) dalam satuan zoom-1 dengan (0,0) = titik kaki di tanah
  function sprite(wx, wy, z, fn) { const [sx, sy] = proj(wx, wy, z); ctx.save(); ctx.translate(sx, sy); ctx.scale(cam.zoom, cam.zoom); fn(); ctx.restore(); }

  // ---------- Tanah (transform bidang; koordinat dunia) ----------
  const bayangTanah = (x, y, r) => { ctx.fillStyle = "rgba(20,40,20,.16)"; ctx.beginPath(); ctx.ellipse(x + 5, y + 4, r, r, 0, 0, TAU); ctx.fill(); };
  function drawTanah(players, now) {
    const { plaza: P, warna, w, h } = layout;
    tanah();
    ctx.fillStyle = warna.rumput; ctx.fillRect(-2600, -2600, w + 5200, h + 5200);
    ctx.fillStyle = warna.rumput2;
    for (const [x, y, l] of rumput) ctx.fillRect(x, y, 2.2, l);
    const cx = P.x + P.w / 2, cy = P.y + P.h / 2;
    ctx.fillStyle = warna.paving[0];
    ctx.fillRect(-2600, cy - 34, P.x + 2600, 68); ctx.fillRect(P.x + P.w, cy - 34, w - P.x - P.w + 2600, 68);
    ctx.fillRect(cx - 34, -2600, 68, P.y + 2600); ctx.fillRect(cx - 34, P.y + P.h, 68, h - P.y - P.h + 2600);
    ctx.save();
    ctx.beginPath(); ctx.roundRect(P.x, P.y, P.w, P.h, P.r); ctx.fillStyle = warna.paving[0]; ctx.fill(); ctx.clip();
    ctx.fillStyle = warna.paving[1];
    for (let i = 0; i < P.w / 48; i++) for (let j = 0; j < P.h / 48; j++) if ((i + j) % 2) ctx.fillRect(P.x + i * 48, P.y + j * 48, 48, 48);
    ctx.restore();
    ctx.fillStyle = "rgba(60,40,20,.10)"; ctx.beginPath(); ctx.roundRect(P.x - 4, P.y - 4, P.w + 8, P.h + 8, P.r + 4); ctx.roundRect(P.x, P.y, P.w, P.h, P.r); ctx.fill("evenodd");
    for (const b of layout.bunga) {
      ctx.fillStyle = "#8d6e63"; ctx.beginPath(); ctx.ellipse(b.x, b.y, b.rx, b.rx * 0.8, 0, 0, TAU); ctx.fill();
      ctx.fillStyle = "#6fae5c"; ctx.beginPath(); ctx.ellipse(b.x, b.y, b.rx - 5, b.rx * 0.8 - 5, 0, 0, TAU); ctx.fill();
    }
    // bayangan statis & pemain di bidang tanah
    for (const p of layout.pohon) bayangTanah(p.x, p.y, 17 * p.s);
    for (const l of layout.lampu) bayangTanah(l.x, l.y, 5);
    for (const l of layout.lapak) bayangTanah(l.x, l.y + 8, 40);
    for (const b of layout.bangku) { ctx.fillStyle = "rgba(20,40,20,.16)"; ctx.beginPath(); ctx.ellipse(b.x + 4, b.y + 3, b.arah === 0 ? 28 : 12, b.arah === 0 ? 12 : 28, 0, 0, TAU); ctx.fill(); }
    bayangTanah(layout.air_mancur.x, layout.air_mancur.y, layout.air_mancur.r + 4);
    bayangTanah(layout.landmark.x, layout.landmark.y, layout.landmark.r);
    for (const p of players.values()) { const pos = posisiPemain(p, now, layout.kecepatan); if (!p.duduk) bayangTanah(pos.x, pos.y, 12); }
  }

  // ---------- Objek ----------
  function drawPohon(p, t) {
    const sway = Math.sin(t * 0.8 + p.x) * 1.5;
    sprite(p.x, p.y, 0, () => {
      const s2 = p.s;
      if (p.jenis === "palem") {
        ctx.strokeStyle = "#9a6b3a"; ctx.lineWidth = 5 * s2; ctx.beginPath(); ctx.moveTo(0, 0); ctx.quadraticCurveTo(6 * s2, -30 * s2, 10 * s2, -52 * s2); ctx.stroke();
        for (let i = 0; i < 6; i++) { const a = i / 6 * TAU + sway * 0.03; ctx.fillStyle = i % 2 ? "#4f8f45" : "#6fae5c"; ctx.beginPath(); ctx.ellipse(10 * s2 + Math.cos(a) * 14 * s2, -52 * s2 + Math.sin(a) * 7 * s2, 16 * s2, 5 * s2, a, 0, TAU); ctx.fill(); }
        ctx.fillStyle = "#7a5a3a"; ctx.beginPath(); ctx.arc(8 * s2, -50 * s2, 3 * s2, 0, TAU); ctx.arc(13 * s2, -49 * s2, 3 * s2, 0, TAU); ctx.fill();
      } else if (p.jenis === "cemara") {
        ctx.fillStyle = "#7a4e26"; ctx.fillRect(-3 * s2, -14 * s2, 6 * s2, 14 * s2);
        ["#3f7a3a", "#4f8f45", "#6fae5c"].forEach((col, k) => { const yy = -12 * s2 - k * 14 * s2, r = (22 - k * 5) * s2; ctx.fillStyle = col; ctx.beginPath(); ctx.moveTo(-r + sway * 0.2, yy); ctx.lineTo(r + sway * 0.2, yy); ctx.lineTo(sway, yy - 22 * s2); ctx.closePath(); ctx.fill(); });
      } else {
        ctx.fillStyle = "#7a4e26"; ctx.fillRect(-3.5 * s2, -20 * s2, 7 * s2, 20 * s2);
        ctx.fillStyle = "#4f8f45"; ctx.beginPath(); ctx.arc(sway * 0.4, -32 * s2, 21 * s2, 0, TAU); ctx.fill();
        ctx.fillStyle = "#6fae5c"; ctx.beginPath(); ctx.arc(-11 * s2 + sway * 0.6, -26 * s2, 14 * s2, 0, TAU); ctx.arc(11 * s2 + sway * 0.6, -25 * s2, 14 * s2, 0, TAU); ctx.fill();
        ctx.fillStyle = "#8cc46d"; ctx.beginPath(); ctx.arc(-4 * s2 + sway, -40 * s2, 9 * s2, 0, TAU); ctx.fill();
      }
    });
  }
  function drawLampu(l, malam) {
    sprite(l.x, l.y, 0, () => {
      ctx.fillStyle = "#4b5563"; ctx.beginPath(); ctx.ellipse(0, 0, 5, 2.5, 0, 0, TAU); ctx.fill(); ctx.fillRect(-1.5, -38, 3, 38);
      ctx.fillStyle = malam > 0.3 ? "#fff3b0" : "#e5e7eb"; ctx.beginPath(); ctx.arc(0, -41, 6, 0, TAU); ctx.fill();
      ctx.fillStyle = "#374151"; ctx.beginPath(); ctx.arc(0, -42, 7, Math.PI, 0); ctx.fill();
    });
  }
  function drawLapak(l) {
    const y = l.y + 8;
    for (const [dx, dy] of [[-30, -14], [30, -14], [-30, 18], [30, 18]]) balok(l.x + dx, y + dy, 3, 3, 0, 46, "#8d6e63");
    balok(l.x, y + 4, 64, 24, 0, 18, "#c99b66", "#dcb98a");
    WARNA_LAPAK_DAGANG.forEach((col, i) => balok(l.x - 21 + i * 14, y + 2, 10, 10, 18, 6 + (i % 2) * 3, col));
    const { T } = balok(l.x, y, 76, 40, 46, 4, l.warna, "#ffffff");
    for (let i = 0; i < 8; i += 2) {
      const q = k => [[T[0][0] + (T[1][0] - T[0][0]) * k, T[0][1] + (T[1][1] - T[0][1]) * k], [T[3][0] + (T[2][0] - T[3][0]) * k, T[3][1] + (T[2][1] - T[3][1]) * k]];
      const [p0, p3] = q(i / 8), [p1, p2] = q((i + 1) / 8); ctx.fillStyle = l.warna; poly([p0, p1, p2, p3]);
    }
  }
  function drawBangku(b) {
    const a = layout.air_mancur, horiz = b.arah === 0;
    const belakang = horiz ? (Math.sign(b.y - a.y) || 1) : (Math.sign(b.x - a.x) || 1);   // sandaran menjauhi air mancur
    if (horiz) { balok(b.x - 20, b.y, 4, 12, 0, 10, "#5c4a3a"); balok(b.x + 20, b.y, 4, 12, 0, 10, "#5c4a3a"); }
    else { balok(b.x, b.y - 20, 12, 4, 0, 10, "#5c4a3a"); balok(b.x, b.y + 20, 12, 4, 0, 10, "#5c4a3a"); }
    balok(b.x, b.y, horiz ? 50 : 16, horiz ? 16 : 50, 10, 4, "#b8763b", "#c98a4a");
    if (horiz) balok(b.x, b.y + belakang * 7, 50, 3, 14, 14, "#a66a33"); else balok(b.x + belakang * 7, b.y, 3, 50, 14, 14, "#a66a33");
  }
  function drawAirMancur(a, t) {
    const z = cam.zoom;
    silinder(a.x, a.y, a.r, 0, 10, "#d6cdb9", "#bfb49d");
    const [wx, wy] = proj(a.x, a.y, 8), rr = (a.r - 8) * z;
    ctx.fillStyle = "#5c96c8"; elips(wx, wy, rr, rr * SQ);
    ctx.fillStyle = "#78b4dc"; elips(wx - 4 * z, wy - 3 * z, rr * 0.7, rr * 0.7 * SQ);
    ctx.strokeStyle = "rgba(255,255,255,.45)"; ctx.lineWidth = 1.5;
    for (let i = 0; i < 3; i++) { const k = (t * 0.32 + i / 3) % 1, r2 = (14 + k * (a.r - 26)) * z; ctx.globalAlpha = 1 - k; ctx.beginPath(); ctx.ellipse(wx, wy, r2, r2 * SQ, 0, 0, TAU); ctx.stroke(); }
    ctx.globalAlpha = 1;
    for (let i = 0; i < 7; i++) { const ang = i * 0.9 + 0.3, r2 = 18 + (i % 3) * 9; const [kx, ky] = proj(a.x + Math.cos(ang) * r2, a.y + Math.sin(ang) * r2, 8); ctx.fillStyle = "rgba(217,162,27,.8)"; elips(kx, ky, 2.6 * z, 1.5 * z); }
    silinder(a.x, a.y, 13, 8, 16, "#cfc6b4", "#e6dfd0");
    silinder(a.x, a.y, 8, 24, 5, "#e6dfd0", "#f1ece0");
    const [jx, jy] = proj(a.x, a.y, 29), j = (16 + Math.sin(t * 4) * 3) * z;
    ctx.fillStyle = "rgba(255,255,255,.85)"; elips(jx, jy - j / 2, 3 * z, j / 2);
    for (let i = 0; i < 6; i++) { const ang = i / 6 * TAU + t * 0.6, hgt = (10 + Math.sin(t * 5 + i) * 4) * z; const [dx, dy] = proj(a.x + Math.cos(ang) * 10, a.y + Math.sin(ang) * 10, 22); ctx.fillStyle = "rgba(255,255,255,.65)"; elips(dx, dy - hgt / 2, 2 * z, hgt / 2); }
  }
  function drawLandmark(lm) {
    const { x, y } = lm, a = layout.air_mancur, z = cam.zoom;
    if (lm.jenis === "gazebo") {
      silinder(x, y, 52, 0, 8, "#d9d2bd", "#e6dfca");
      for (let i = 0; i < 6; i++) { const ang = i / 6 * TAU + Math.PI / 6; balok(x + Math.cos(ang) * 38, y + Math.sin(ang) * 38, 5, 5, 8, 56, "#8d6e63"); }
      kerucut(x, y, 64, 64, 40, "#c96d47");
      const [fx0, fy0] = proj(x, y, 106); ctx.fillStyle = "#d9a21b"; elips(fx0, fy0, 4 * z, 4 * z);
    } else if (lm.jenis === "tugu") {
      balok(x, y, 64, 40, 0, 12, "#cfc6b4", "#dcd4c2"); balok(x, y, 44, 28, 12, 8, "#b8ae9a", "#c9c0ad");
      balok(x, y, 16, 16, 20, 72, "#e8e2d2", "#f1ece0"); kerucut(x, y, 12, 92, 16, "#d9a21b");
      sprite(x, y, 52, () => { ctx.fillStyle = "#1f2a44"; ctx.fillRect(-8, -6, 16, 12); ctx.fillStyle = "#ffffff"; ctx.font = `800 9px ${FONT}`; ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText("J", 0, 0.5); });
    } else if (lm.jenis === "panggung") {
      const sisi = Math.sign(x - a.x) || 1;                                   // panggung menghadap air mancur, latar di sisi luar
      balok(x, y, 44, 132, 0, 22, "#a9734e", "#c08a5e");
      const { B, vis } = balok(x + sisi * 19, y, 6, 132, 22, 64, "#0000CD", "#1a1aa6");
      const i = sisi > 0 ? 3 : 1;                                             // sisi yang menghadap air mancur
      if (vis[i]) teksSisi(B[i], B[(i + 1) % 4], 64 * z, "PANGGUNG", "#ffffff", 13 * z);
      balok(x - sisi * 14, y - 56, 10, 10, 22, 20, "#141414"); balok(x - sisi * 14, y + 56, 10, 10, 22, 20, "#141414");
    } else {
      balok(x, y, 44, 44, 0, 6, "#dcd6c6", "#e6e0d0"); balok(x, y, 36, 36, 6, 98, "#f4f1e6", "#ede8d8"); kerucut(x, y, 28, 104, 26, "#0000CD");
      sprite(x, y, 84, () => {
        ctx.fillStyle = "#ffffff"; ctx.beginPath(); ctx.arc(0, 0, 12, 0, TAU); ctx.fill(); ctx.strokeStyle = "#141414"; ctx.lineWidth = 1.5; ctx.stroke();
        const d = new Date(), hA = ((d.getHours() % 12) + d.getMinutes() / 60) / 12 * TAU - Math.PI / 2, mA = d.getMinutes() / 60 * TAU - Math.PI / 2;
        ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(Math.cos(hA) * 6, Math.sin(hA) * 6); ctx.moveTo(0, 0); ctx.lineTo(Math.cos(mA) * 9, Math.sin(mA) * 9); ctx.stroke();
      });
    }
  }
  function drawBunga(b) {
    const z = cam.zoom;
    for (let i = 0; i < 9; i++) {
      const ang = i / 9 * TAU, rr = 0.55 + (i % 3) * 0.15;
      const [fx0, fy0] = proj(b.x + Math.cos(ang) * (b.rx - 9) * rr, b.y + Math.sin(ang) * (b.rx * 0.8 - 7) * rr, 4);
      ctx.fillStyle = i % 4 === 3 ? "#f8fafc" : b.warna; elips(fx0, fy0, 3 * z, 3 * z);
      ctx.fillStyle = "#4f8f45"; ctx.fillRect(fx0 - 0.6 * z, fy0, 1.2 * z, 4 * z);
    }
  }

  // ---------- Efek ----------
  function drawFx(now) {
    const z = cam.zoom, a = layout.air_mancur;
    for (let i = fx.length - 1; i >= 0; i--) {
      const f = fx[i], k = (now - f.at) / f.ms;
      if (k >= 1) { fx.splice(i, 1); continue; }
      if (f.jenis === "tujuan") { const [sx, sy] = proj(f.x, f.y), r = 16 * z * (1 - k * 0.6); ctx.strokeStyle = `rgba(0,0,205,${0.7 * (1 - k)})`; ctx.lineWidth = 2; ctx.beginPath(); ctx.ellipse(sx, sy, r, r * SQ, 0, 0, TAU); ctx.stroke(); }
      else if (f.jenis === "koin") {
        if (k < 0.7) {
          const q = k / 0.7, [sx, sy] = proj(f.x + (f.ke.x - f.x) * q, f.y + (f.ke.y - f.y) * q, 30 + Math.sin(q * Math.PI) * 80);
          ctx.fillStyle = "#d9a21b"; elips(sx, sy, 5 * z, 5 * z * Math.abs(Math.cos(q * 9))); ctx.strokeStyle = "#b7791f"; ctx.lineWidth = 1; ctx.stroke();
        } else { const q = (k - 0.7) / 0.3, [sx, sy] = proj(a.x, a.y, 8), r = (6 + q * 22) * z; ctx.strokeStyle = `rgba(255,255,255,${0.9 * (1 - q)})`; ctx.lineWidth = 2; ctx.beginPath(); ctx.ellipse(sx, sy, r, r * SQ, 0, 0, TAU); ctx.stroke(); }
      }
    }
  }

  // ---------- Label layar: nama, gelembung obrolan, emote ----------
  function wrap(teks, maxW) {
    const kata = teks.split(/\s+/), baris = []; let cur = "";
    for (const k of kata) { const uji = cur ? `${cur} ${k}` : k; if (ctx.measureText(uji).width > maxW && cur) { baris.push(cur); cur = k; } else cur = uji; }
    if (cur) baris.push(cur);
    return baris.slice(0, 4);
  }
  function drawTag(tg, now) {
    const { p, sx, sy, emoteAktif, dancing, duduk } = tg;
    if (sx < -200 || sx > W + 200 || sy < -120 || sy > H + 120) return;
    const top = sy - (duduk ? 44 : 50) * cam.zoom;
    const label = `@${p.username || p.nama}`;
    ctx.font = `700 10.5px ${FONT}`; ctx.textAlign = "center"; ctx.textBaseline = "middle";
    const lw = ctx.measureText(label).width + 14;
    // pil nama seragam untuk semua pemain: kaca abu-abu transparan (tanpa warna khusus untuk diri sendiri / yang dipilih)
    ctx.fillStyle = "rgba(38,38,44,.52)";
    ctx.beginPath(); ctx.roundRect(sx - lw / 2, top - 24, lw, 18, 9); ctx.fill();
    ctx.strokeStyle = "rgba(255,255,255,.28)"; ctx.lineWidth = 1; ctx.stroke();
    ctx.fillStyle = "#fff"; ctx.fillText(label, sx, top - 15);
    let atas = top - 24;
    if (p.bubble && now - p.bubble.at < BUBBLE_MS) {
      const sisa = BUBBLE_MS - (now - p.bubble.at), alpha = Math.min(1, sisa / 600);
      ctx.font = `600 11.5px ${FONT}`;
      const baris = wrap(p.bubble.teks, 160), bw = Math.min(176, Math.max(...baris.map(b => ctx.measureText(b).width)) + 18), bh = baris.length * 14 + 12;
      ctx.globalAlpha = alpha;
      ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.roundRect(sx - bw / 2, atas - 8 - bh, bw, bh, 12); ctx.fill();
      ctx.beginPath(); ctx.moveTo(sx - 5, atas - 8); ctx.lineTo(sx + 5, atas - 8); ctx.lineTo(sx, atas - 3); ctx.closePath(); ctx.fill();
      ctx.fillStyle = "#141414"; baris.forEach((b, i) => ctx.fillText(b, sx, atas - 8 - bh + 13 + i * 14));
      ctx.globalAlpha = 1;
      atas = atas - 8 - bh;
    }
    if (emoteAktif) {
      const k = Math.min(1, (now - p.emote_at) / 220), sisa = EMOTE_MS - (now - p.emote_at), a = Math.min(1, sisa / 400);
      const ex = sx + lw / 2 + 17, ey = top - 17 - (1 - k) * 8;
      ctx.globalAlpha = a; ctx.save(); ctx.translate(ex, ey); ctx.scale(k, k);
      ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.arc(0, 0, 14, 0, TAU); ctx.fill();
      ctx.fillStyle = "rgba(0,0,0,.08)"; ctx.beginPath(); ctx.arc(0, 1.5, 14, 0, Math.PI); ctx.fill();
      drawEmoteGlyph(ctx, emoteAktif, 0, 0, 9);
      ctx.restore(); ctx.globalAlpha = 1;
    }
    if (dancing) {
      for (let i = 0; i < 2; i++) { const q = ((now / 900) + i * 0.5) % 1; ctx.globalAlpha = 1 - q; drawEmoteGlyph(ctx, "joget", sx + (i ? 26 : -26), top - 10 - q * 40, 6); }
      ctx.globalAlpha = 1;
    }
  }

  function drawMalam(malam) {
    if (malam <= 0.02) return;
    ctx.fillStyle = `rgba(18,28,72,${0.3 * malam})`; ctx.fillRect(0, 0, W, H);
    for (const l of layout.lampu) {
      const [sx, sy] = proj(l.x, l.y, 41), r = 80 * cam.zoom;
      const g = ctx.createRadialGradient(sx, sy, 2, sx, sy, r); g.addColorStop(0, `rgba(255,230,150,${0.55 * malam})`); g.addColorStop(1, "rgba(255,230,150,0)");
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(sx, sy, r, 0, TAU); ctx.fill();
    }
    const a = layout.air_mancur, [ax, ay] = proj(a.x, a.y, 8), ar = (a.r + 30) * cam.zoom;
    const g2 = ctx.createRadialGradient(ax, ay, 2, ax, ay, ar); g2.addColorStop(0, `rgba(120,200,255,${0.35 * malam})`); g2.addColorStop(1, "rgba(120,200,255,0)");
    ctx.fillStyle = g2; ctx.beginPath(); ctx.ellipse(ax, ay, ar, ar * 0.7, 0, 0, TAU); ctx.fill();
  }

  // arah hadap sprite (±1 di layar) dari arah gerak dunia; dipertahankan saat diam
  function hadap(p, pos) {
    if (pos.bergerak) { const fx0 = c * pos.dx - s * pos.dy; if (Math.abs(fx0) > 0.08) p._hadap = fx0 < 0 ? -1 : 1; }
    return p._hadap || 1;
  }

  // ---------- Render utama ----------
  function render(players, meId, now, opts = {}) {
    if (!layout) return;
    const t = now / 1000;
    const dr = cam.rotTarget - cam.rot;
    cam.rot = Math.abs(dr) < 0.0005 ? cam.rotTarget : cam.rot + dr * 0.12;
    c = Math.cos(cam.rot); s = Math.sin(cam.rot);
    if (follow && meId && players.has(meId)) {
      const me = posisiPemain(players.get(meId), now, layout.kecepatan);
      cam.x += (me.x - cam.x) * 0.1; cam.y += (me.y - cam.y) * 0.1;
    }
    clampCam();
    drawTanah(players, now);
    layar();
    const ents = [], tags = [];
    layout.pohon.forEach(p => ents.push({ d: depth(p.x, p.y), f: () => drawPohon(p, t) }));
    layout.lampu.forEach(l => ents.push({ d: depth(l.x, l.y), f: () => drawLampu(l, opts.malam || 0) }));
    layout.lapak.forEach(l => ents.push({ d: depth(l.x, l.y + 12), f: () => drawLapak(l) }));
    layout.bangku.forEach(b => ents.push({ d: depth(b.x, b.y), f: () => drawBangku(b) }));
    layout.bunga.forEach(b => ents.push({ d: depth(b.x, b.y), f: () => drawBunga(b) }));
    ents.push({ d: depth(layout.air_mancur.x, layout.air_mancur.y), f: () => drawAirMancur(layout.air_mancur, t) });
    ents.push({ d: depth(layout.landmark.x, layout.landmark.y), f: () => drawLandmark(layout.landmark) });
    for (const p of players.values()) {
      const pos = posisiPemain(p, now, layout.kecepatan), duduk = !!p.duduk, facing = hadap(p, pos);
      const emoteAktif = p.emote && now - p.emote_at < EMOTE_MS ? p.emote : null, dancing = (p.joget_sampai || 0) > now;
      const [sx, sy] = proj(pos.x, pos.y, duduk ? 10 : 0);
      ents.push({ d: depth(pos.x, pos.y) + 0.01, f: () => {
        if (p.id === meId || p.id === opts.pilih) { ctx.strokeStyle = p.id === meId ? "rgba(0,0,205,.55)" : "rgba(217,162,27,.8)"; ctx.lineWidth = 2; ctx.beginPath(); ctx.ellipse(sx, sy, 17 * cam.zoom, 17 * cam.zoom * SQ, 0, 0, TAU); ctx.stroke(); }
        ctx.save(); ctx.translate(sx, sy); ctx.scale(cam.zoom, cam.zoom);
        drawAvatar(ctx, 0, 0, p.avatar, { t: t + hash01(p.id) * 7, walking: pos.bergerak && !duduk, facing, sitting: duduk, dancing, emote: emoteAktif });
        ctx.restore();
      } });
      tags.push({ p, sx, sy, emoteAktif, dancing, duduk });
    }
    ents.sort((a, b) => a.d - b.d).forEach(e => e.f());
    drawFx(now);
    drawMalam(opts.malam || 0);
    tags.sort((a, b) => a.sy - b.sy).forEach(tg => drawTag(tg, now));
  }

  // ---------- Uji ketukan (koordinat layar) ----------
  function hit(sx, sy, players, now) {
    const z = cam.zoom;
    let best = null, bd = 1e9;
    for (const p of players.values()) {                                      // kotak sprite karakter (lebar ±17, tinggi 52)
      const pos = posisiPemain(p, now, layout.kecepatan), [px, py] = proj(pos.x, pos.y, p.duduk ? 10 : 0);
      const dx = sx - px, dy = sy - py;
      if (Math.abs(dx) < 17 * z + 6 && dy > -52 * z - 6 && dy < 8 * z) { const d = Math.abs(dx) + Math.abs(dy + 26 * z); if (d < bd) { best = p.id; bd = d; } }
    }
    if (best) return { jenis: "pemain", id: best };
    const rSlot = Math.max(24, 20 * z);                                      // area ketuk bangku minimal 24 px (HP sempit)
    for (const b of layout.bangku) for (let i = 0; i < 2; i++) { const [bx, by] = proj(b.slot[i][0], b.slot[i][1], 10); if (Math.hypot(sx - bx, sy - by) < rSlot) return { jenis: "bangku", b: b.id, slot: i }; }
    const a = layout.air_mancur, [ax, ay] = proj(a.x, a.y, 8), rx = (a.r + 16) * z, ry = rx * SQ + 22 * z;
    if (((sx - ax) / rx) ** 2 + ((sy - ay) / ry) ** 2 < 1) return { jenis: "air_mancur" };
    const [wx, wy] = unproj(sx, sy);
    return { jenis: "tanah", x: wx, y: wy };
  }

  return {
    cam,
    setLayout(l) {
      layout = l; cam.x = l.air_mancur.x; cam.y = l.air_mancur.y + 40; cam.zoom = W < 600 ? 1.25 : 1.45;
      rumput = []; for (let i = 0; i < 520; i++) { const a = hash01(`a${i}`), b = hash01(`b${i}`); rumput.push([a * l.w, b * l.h, 4 + hash01(`c${i}`) * 5]); }
      clampCam();
    },
    render, hit, proj, unproj,
    toScreen: (wx, wy) => proj(wx, wy, 0),
    pan(dx, dy) { follow = false; const rx = -dx / cam.zoom, ry = -dy / (cam.zoom * SQ); cam.x += c * rx + s * ry; cam.y += -s * rx + c * ry; clampCam(); },
    zoomAtPoint(f, sx, sy) { const [wx, wy] = unproj(sx, sy); cam.zoom = Math.min(2.4, Math.max(0.6, cam.zoom * f)); const [nx, ny] = unproj(sx, sy); cam.x += wx - nx; cam.y += wy - ny; clampCam(); },
    putar(d, langsung = false) { cam.rotTarget += d; if (langsung) { cam.rot = cam.rotTarget; c = Math.cos(cam.rot); s = Math.sin(cam.rot); } },
    lihat(wx, wy) { follow = false; cam.x = wx; cam.y = wy; clampCam(); },
    ikuti(v = true) { follow = v; },
    tambahFx(f) { fx.push(f); },
    destroy() { ro.disconnect(); },
  };
}
